import { useState } from 'react';
import { Box, Typography, Button, InputBase, IconButton, useTheme } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import moment from 'moment-timezone';
import { ReactComponent as CalendarOutlineBlueIcon } from '../../../assets/PepWise2.0/calendar-outline_blue.svg';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { calculateRange, useDateTimeRangePicker, toCalendarDate, formatMomentDate } from './useDateTimeRangePicker'
import { TIMEZONE_MAP } from 'utils/common-util';

export const DEFAULT_FIXED_OPTIONS = [
  { val: 'today', label: 'Today' },
  { val: '24h', label: 'Last 24 Hrs' },
  { val: '7d', label: 'Last 7 Days' },
  { val: '30d', label: 'Last 30 Days' },
  { val: '3m', label: 'Last 3 Months' }
] as const;

export const OLDER_THAN_90_DAYS_OPTION = {
  val: 'more_than_90_days',
  label: 'Older than 90 days'
} as const;

export const EWS_INCIDENTS_FIXED_OPTIONS = [
  ...DEFAULT_FIXED_OPTIONS,
  OLDER_THAN_90_DAYS_OPTION
] as const;

const DateRangeSidebar = ({
  selectedRange,
  handleFixedRange,
  handleCustomRange,
  fixedOptions,
  showCustomRange = true,
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  const options: { val: string; label: string }[] = fixedOptions ?? DEFAULT_FIXED_OPTIONS;
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: { xs: '100%', md: '200px', lg: '220px' },
        minWidth: { xs: '100%', md: '200px', lg: '220px' },
        maxWidth: { xs: '100%', md: '200px', lg: '220px' },
        backgroundColor: colors.dateFilter.presetPanelBackground,
        borderRight: { xs: 'none', md: `1px solid ${colors.dateFilter.popoverBorder}` },
        borderBottom: { xs: `1px solid ${colors.dateFilter.popoverBorder}`, md: 'none' },
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        zIndex: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          p: { xs: '12px 16px 6px 16px', md: '24px 20px 8px 20px' },
          fontSize: { xs: '13px', md: '15px', lg: '16px' },
          color: colors.dateFilter.presetText,
          fontFamily: 'Montserrat',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Fixed Time Range
      </Typography>

      {/* Button list container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            flexWrap: 'nowrap',
            overflowX: { xs: 'auto', md: 'hidden' },
            overflowY: { xs: 'visible', md: 'auto' },
            maxHeight: { md: '330px' },
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px', 
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.dateFilter.triggerHoverBackground,
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            msOverflowStyle: 'none',
            flex: 1,
            pb: { xs: 1, md: 0 },
            px: { xs: 1, md: 0 },

            width: '100%',
          }}
        >
          {(() => {
             const defaultOptions = [
                { val: '5m', label: 'Last 5 Mins' },
                { val: '10m', label: 'Last 10 Mins' },
                { val: '15m', label: 'Last 15 Mins' },
                { val: '30m', label: 'Last 30 Mins' },
                { val: '1h', label: 'Last 1 Hour' },
                { val: 'today', label: 'Today' },
                { val: '24h', label: 'Last 24 Hrs' },
                { val: '7d', label: 'Last 7 Days' },
                { val: '30d', label: 'Last 30 Days' },
                { val: '90d', label: 'Last 90 Days' },
                { val: 'previousyear', label: 'Previous Year' },
             ];

             const normalizeToShorthand = (val: string): string | null => {
                const match = val.match(/^last[_ ](\d+)[_ ](minutes?|mins?|hours?|hrs?|days?)$/i);
                if (!match) return null;
                const num = match[1];
                const unit = match[2].toLowerCase();
                if (unit.startsWith('min')) return `${num}m`;
                if (unit.startsWith('h')) return `${num}h`;
                if (unit.startsWith('d')) return `${num}d`;
                return null;
              };
             const options = [...(fixedOptions ?? defaultOptions)];
             const shorthand = selectedRange ? normalizeToShorthand(selectedRange) : null;
             const effectiveRange =
               shorthand && options.some((o) => o.val === shorthand) ? shorthand : selectedRange;

             if (
               selectedRange &&
               selectedRange !== 'custom' &&
               !options.some((o) => o.val === selectedRange) &&
               !shorthand
             ) {
               let clean = String(selectedRange)
                 .replace(/_/g, ' ')
                 .replace(/^last\s+/i, '');
               let dynamicLabel = `Last ${clean.charAt(0).toUpperCase() + clean.slice(1)}`;

               options.push({ val: selectedRange, label: dynamicLabel });
             }
             return options.map(({ val, label }) => (
          <Button
            key={val}
            onClick={() => handleFixedRange(val)}
            sx={{
              justifyContent: { xs: 'center', md: 'flex-start' },
              textTransform: 'none',
              color: colors.dateFilter.presetText,
              backgroundColor: effectiveRange === val ? colors.dateFilter.presetSelectedBackground : 'transparent',
              borderRadius: { xs: '20px', md: '0px' },
              py: { xs: '6px', md: '10px' },
              px: { xs: '14px', md: '20px' },
              mx: { xs: '4px', md: 0 },
              my: { xs: '2px', md: 0 },
              fontWeight: effectiveRange === val ? 600 : 400,
              fontSize: { xs: '12px', md: '14px', lg: '15px' },
              fontFamily: 'Montserrat',
              whiteSpace: 'nowrap',
              // xs: shrink-0 chip; md+: full width fills sidebar
              flexShrink: 0,
              width: { xs: 'auto', md: '100%' },
              minWidth: { xs: 'auto', md: 0 },
              maxWidth: { xs: 'none', md: '100%' },
              border: {
                xs: effectiveRange === val ? `1px solid ${colors.dateFilter.rangeBackground}` : `1px solid ${colors.dateFilter.popoverBorder}`,
                md: 'none',
              },
              '@media (hover: hover)': {
                '&:hover': { backgroundColor: colors.dateFilter.presetSelectedBackground, color: colors.dateFilter.presetText },
              },
              '&:focus, &:active, &.Mui-focusVisible': {
                color: colors.dateFilter.presetText,
              },
            }}
          >
            {label}
          </Button>
        ));
          })()}

        <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minWidth: 0 }} />

        {/* Custom Date Range */}
        {showCustomRange && (
        <Button
          onClick={handleCustomRange}
          sx={{
            justifyContent: { xs: 'center', md: 'space-between' },
            textTransform: 'none',
            color: colors.dateFilter.presetText,
            backgroundColor: selectedRange === 'custom' ? colors.dateFilter.presetSelectedBackground : 'transparent',
            borderRadius: { xs: '20px', md: '0px' },
            py: { xs: '6px', md: '16px' },
            px: { xs: '14px', md: '20px' },
            mx: { xs: '4px', md: 0 },
            my: { xs: '2px', md: 0 },
            fontWeight: selectedRange === 'custom' ? 600 : 400,
            fontSize: { xs: '12px', md: '14px', lg: '15px' },
            fontFamily: 'Montserrat',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            width: { xs: 'auto', md: '100%' },
            minWidth: { xs: 'auto', md: 0 },
            maxWidth: { xs: 'none', md: '100%' },
            border: {
              xs: selectedRange === 'custom' ? `1px solid ${colors.dateFilter.rangeBackground}` : `1px solid ${colors.dateFilter.popoverBorder}`,
              md: 'none',
            },
            borderTop: { xs: 'none', md: `1px solid ${colors.dateFilter.popoverBorder}` },
            '@media (hover: hover)': {
              '&:hover': { backgroundColor: colors.dateFilter.presetSelectedBackground, color: colors.dateFilter.presetText },
            },
            '&:focus, &:active, &.Mui-focusVisible': {
              color: colors.dateFilter.presetText,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'space-between' },
              width: '100%',
              minWidth: 0,
              gap: { xs: 0, md: 1 },
            }}
          >
            <Typography
              sx={{
                fontWeight: 'inherit',
                fontSize: 'inherit',
                color: colors.dateFilter.presetText,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Custom Date Range
            </Typography>
            <ChevronRightIcon
              sx={{
                fontSize: '18px',
                color: colors.dateFilter.presetArrow,
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
              }}
            />
          </Box>
        </Button>
        )}
      </Box>
    </Box>
  );
};

const AbsoluteInputs = ({
  isCustom,
  startDateInput,
  endDateInput,
  handleDateChange,
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: { xs: '100%', md: '210px', lg: '250px' },
        minWidth: { xs: 0, md: '210px', lg: '250px' },
        maxWidth: { xs: '100%', md: '210px', lg: '250px' },
        p: { xs: '16px 16px 12px 16px', md: '24px 16px', lg: '24px' },
        borderRight: { xs: 'none', md: `1px solid ${colors.dateFilter.popoverBorder}` },
        borderBottom: { xs: `1px solid ${colors.dateFilter.popoverBorder}`, md: 'none' },
        boxSizing: 'border-box',
        opacity: 1,
        overflow: 'hidden',
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          mb: { xs: 2, md: 3, lg: 4 },
          fontSize: { xs: '13px', md: '14px', lg: '15px' },
          color: colors.dateFilter.dayText,
          opacity: isCustom ? 1 : 0.5,
          fontFamily: 'Montserrat',
          transition: 'color 0.2s ease',
        }}
      >
        Absolute Time Range
      </Typography>

      {/* Start Date */}
      <Box sx={{ position: 'relative', mb: { xs: 3, md: 3, lg: 4 }, mt: { xs: 1, md: 2 } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '-10px',
            left: '12px',
            backgroundColor: colors.dateFilter.popoverBackground,
            px: 0.5,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              color: isCustom ? colors.dateFilter.weekDayText : colors.dateFilter.dayText,
              opacity: isCustom ? 1 : 0.5,
              fontSize: '11px',
              fontStyle: 'italic',
              fontFamily: 'Montserrat',
              transition: 'color 0.2s ease',
            }}
          >
            Start Date
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${colors.dateFilter.popoverBorder}`,
            borderRadius: '4px',
            px: 1.5,
            py: { xs: 1, md: 1.25 },
            backgroundColor: colors.dateFilter.popoverBackground,
            transition: 'border-color 0.2s ease',
            overflow: 'hidden',
          }}
        >
          <InputBase
            value={startDateInput}
            onChange={(e) => handleDateChange('from')(e.target.value)}
            placeholder="MM-DD-YYYY HH:mm"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: { xs: '12px', md: '12px', lg: '14px' },
              fontWeight: 600,
              fontFamily: 'Montserrat',
              '& .MuiInputBase-input': {
                color: isCustom ? `${colors.dateFilter.dayText} !important` : `${colors.dateFilter.weekDayText} !important`,
                WebkitTextFillColor: isCustom ? `${colors.dateFilter.dayText} !important` : `${colors.dateFilter.weekDayText} !important`,
                opacity: isCustom ? 1 : 0.5,
                transition: 'color 0.2s ease',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
          <Box
            component={CalendarOutlineBlueIcon}
            sx={{
              width: { xs: '16px', md: '16px', lg: '20px' },
              height: { xs: '16px', md: '16px', lg: '20px' },
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      {/* End Date */}
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: '-10px',
            left: '12px',
            backgroundColor: colors.dateFilter.popoverBackground,
            px: 0.5,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              color: isCustom ? colors.dateFilter.weekDayText : colors.dateFilter.dayText,
              opacity: isCustom ? 1 : 0.5,
              fontSize: '11px',
              fontStyle: 'italic',
              fontFamily: 'Montserrat',
              transition: 'color 0.2s ease',
            }}
          >
            End Date
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${colors.dateFilter.popoverBorder}`,
            borderRadius: '4px',
            px: 1.5,
            py: { xs: 1, md: 1.25 },
            backgroundColor: colors.dateFilter.popoverBackground,
            transition: 'border-color 0.2s ease',
            overflow: 'hidden',
          }}
        >
          <InputBase
            value={endDateInput}
            onChange={(e) => handleDateChange('to')(e.target.value)}
            placeholder="MM-DD-YYYY HH:mm"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: { xs: '12px', md: '12px', lg: '14px' },
              fontWeight: 600,
              fontFamily: 'Montserrat',
              '& .MuiInputBase-input': {
                color: isCustom ? `${colors.dateFilter.dayText} !important` : `${colors.dateFilter.weekDayText} !important`,
                WebkitTextFillColor: isCustom ? `${colors.dateFilter.dayText} !important` : `${colors.dateFilter.weekDayText} !important`,
                opacity: isCustom ? 1 : 0.5,
                transition: 'color 0.2s ease',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
          <Box
            component={CalendarOutlineBlueIcon}
            sx={{
              width: { xs: '16px', md: '16px', lg: '20px' },
              height: { xs: '16px', md: '16px', lg: '20px' },
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};



const CalendarSection = ({
  isCustom,
  displayRange,
  setRange,
  setSelectedRange,
  setStartDateInput,
  setEndDateInput,
  month,
  setMonth,
  ianaTimezone,
  startDateFromBackend,
  availableDates = [],
}: any) => {
  const theme = useTheme();
  const colors = theme.palette;
  const highlightDates = availableDates.map((d: string) => moment.tz(d, ianaTimezone).startOf('day').toDate());
  const BE_FORMAT = 'MM-DD-YY HH:mm';
  const rawMinDate = startDateFromBackend ? moment.tz(startDateFromBackend, BE_FORMAT, ianaTimezone).startOf('day').toDate() : undefined;
  const minDate = rawMinDate ? toCalendarDate(rawMinDate, ianaTimezone) : undefined;

  const isOpsPulse = window.location.pathname.includes('/dataops/unified-incidents') || window.location.pathname.includes('/dataops/unified-alerts') ;
  let disabledRules: any = minDate ? { before: minDate } : undefined;
  if (isOpsPulse) {
    const todayRaw = moment.tz(ianaTimezone).startOf('day').toDate();
    const today = toCalendarDate(todayRaw, ianaTimezone);
    const minRaw = moment.tz(ianaTimezone).subtract(90, 'days').startOf('day').toDate();
    const minDateLimit = toCalendarDate(minRaw, ianaTimezone);
    disabledRules = [{ before: minDateLimit }, { after: today }];
  }

  const modifiers = {
    hasData: highlightDates,
    selected: (date: Date) => {
      if (!displayRange?.from || !displayRange?.to) return false;
      const d = moment(date).startOf('day');
      const from = moment(displayRange.from).startOf('day');
      const to = moment(displayRange.to).startOf('day');
      const isWithinRange = d.isBetween(from, to, 'day', '[]');
      const hasBackendData = minDate ? d.isSameOrAfter(moment(minDate).startOf('day')) : true;
      return isWithinRange && hasBackendData;
    },
    range_start: (date: Date) => {
      if (!displayRange?.from) return false;
      const d = moment(date).startOf('day');
      const from = moment(displayRange.from).startOf('day');
      const hasBackendData = minDate ? d.isSameOrAfter(moment(minDate).startOf('day')) : true;
      return d.isSame(from) && hasBackendData;
    },
    range_middle: (date: Date) => {
      if (!displayRange?.from || !displayRange?.to) return false;
      const d = moment(date).startOf('day');
      const from = moment(displayRange.from).startOf('day');
      const to = moment(displayRange.to).startOf('day');
      const isWithinRange = d.isBetween(from, to, 'day', '()');
      const hasBackendData = minDate ? d.isSameOrAfter(moment(minDate).startOf('day')) : true;
      return isWithinRange && hasBackendData;
    },
    range_end: (date: Date) => {
      if (!displayRange?.to) return false;
      const d = moment(date).startOf('day');
      const to = moment(displayRange.to).startOf('day');
      const hasBackendData = minDate ? d.isSameOrAfter(moment(minDate).startOf('day')) : true;
      return d.isSame(to) && hasBackendData;
    },
  };

  const modifiersStyles = {
    hasData: {
      position: 'relative' as const,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '2px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        backgroundColor: colors.dateFilter.rangeBackground,
      }
    },
    selected: {
      backgroundColor: isCustom ? colors.dateFilter.rangeBackground : colors.dateFilter.presetSelectedBackground,
      color: isCustom ? colors.dateFilter.dayTextInRange : colors.dateFilter.dayText,
      borderRadius: 0,
    },
    range_start: {
      backgroundColor: isCustom ? colors.dateFilter.rangeBackground : colors.dateFilter.presetSelectedBackground,
      color: isCustom ? colors.dateFilter.dayTextInRange : colors.dateFilter.dayText,
      borderRadius: '4px 0 0 4px',
    },
    range_middle: {
      backgroundColor: isCustom ? colors.dateFilter.rangeBackground : colors.dateFilter.presetSelectedBackground,
      color: isCustom ? colors.dateFilter.dayTextInRange : colors.dateFilter.dayText,
      borderRadius: 0,
    },
    range_end: {
      backgroundColor: isCustom ? colors.dateFilter.rangeBackground : colors.dateFilter.presetSelectedBackground,
      color: isCustom ? colors.dateFilter.dayTextInRange : colors.dateFilter.dayText,
      borderRadius: '0 4px 4px 0',
    },
  };
  return (
    <DayPicker
      mode="range"
      selected={displayRange}
      disabled={disabledRules}
      modifiers={modifiers}
      modifiersStyles={modifiersStyles}
      onSelect={
        isCustom
          ? (r) => {
              const isSameAsCurrentSingleDay =
                !r?.from &&
                displayRange?.from &&
                displayRange?.to &&
                moment(displayRange.from).isSame(displayRange.to, 'day');

              if (isSameAsCurrentSingleDay) {
                r = { from: displayRange.from, to: displayRange.to };
              }

              if (r?.from && !r?.to && displayRange?.from) {
                const fromDate = moment(displayRange.from).startOf('day');
                const clickedDate = moment(r.from).startOf('day');
                if (fromDate.isSame(clickedDate)) {
                  r = { from: r.from, to: r.from };
                }
              }

              if (r?.from) {
                const dateStr = `${String(r.from.getMonth() + 1).padStart(2, '0')}-${String(r.from.getDate()).padStart(2, '0')}-${r.from.getFullYear()}`;
                r = {
                  ...r,
                  from: moment
                    .tz(`${dateStr} 00:00`, 'MM-DD-YYYY HH:mm', ianaTimezone)
                    .toDate(),
                };
                setStartDateInput(formatMomentDate(ianaTimezone, r.from));
              } else {
                setStartDateInput('');
              }
              if (r?.to) {
                const dateStr = `${String(r.to.getMonth() + 1).padStart(2, '0')}-${String(r.to.getDate()).padStart(2, '0')}-${r.to.getFullYear()}`;
                r = {
                  ...r,
                  to: moment
                    .tz(`${dateStr} 23:59`, 'MM-DD-YYYY HH:mm', ianaTimezone)
                    .toDate(),
                };
                setEndDateInput(formatMomentDate(ianaTimezone, r.to));
              } else {
                setEndDateInput('');
              }
              setRange(r);
              setSelectedRange('custom');
            }
          : undefined
      }
      month={month}
      onMonthChange={setMonth}
      showOutsideDays
      styles={{
        head_cell: {
          color: colors.dateFilter.weekDayText,
          opacity: 0.6,
          fontWeight: 500,
          fontSize: '11px',
          textTransform: 'none',
          paddingBottom: '4px',
        },
        cell: {
          fontSize: '11px',
          padding: 0,
          width: '14.2857%',
        },
        day: {
          margin: 0,
          width: '100%',
          maxWidth: 'none',
          height: '32px',
          borderRadius: 0,
          cursor: isCustom ? 'pointer' : 'default',
          color: colors.dateFilter.dayText,
          fontFamily: 'Montserrat',
          fontSize: '11px',
        },
      }}
      components={{
        Caption: () => null,
      }}
    />
  );
};

export interface DateTimeRangePickerProps {
  onApply: (range: string, start?: Date, end?: Date) => void;
  onClose: () => void;
  initialRange?: string;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  timezone?: string;
  startDateFromBackend?: string;
  availableDates?: string[];
  fixedOptions?: { val: string; label: string }[];
  olderThan90DaysMode?: boolean;
  showCustomRange?: boolean;
}

const DateTimeRangePicker = ({
  onClose,
  onApply,
  initialRange = 'today',
  initialStartDate,
  initialEndDate,
  timezone = 'EST',
  startDateFromBackend,
  availableDates,
  fixedOptions,
  olderThan90DaysMode = false,
  showCustomRange = true,
}: DateTimeRangePickerProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const ianaTimezone = TIMEZONE_MAP[timezone];
  // Backend format is MM-DD-YY HH:mm as per payload
  const BE_FORMAT = 'MM-DD-YY HH:mm';
  const rawMinDate = startDateFromBackend ? moment.tz(startDateFromBackend, BE_FORMAT, ianaTimezone).startOf('day').toDate() : undefined;
  const minDate = rawMinDate ? toCalendarDate(rawMinDate, ianaTimezone) : undefined;
  const props = {initialRange, initialStartDate, initialEndDate, ianaTimezone, minDate, olderThan90DaysMode};
  const picker = useDateTimeRangePicker(props);
  const {
    range,
    setRange,
    selectedRange,
    setSelectedRange,
    startDateInput,
    endDateInput,
    setStartDateInput,
    setEndDateInput,
    isCustom,
    canGoPrev,
    canGoNext,
    displayRange,
    month,
    setMonth,
    changeMonth,
    handleFixedRange,
    handleCustomRange,
    handleDateChange,
  } = picker;

  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
  const isOkDisabled = fixedOptions ? false : !hasSelectionChanged;

  const wrappedHandleDateChange = (type: 'from' | 'to') => (rawVal: string) => {
    handleDateChange(type)(rawVal);
    setHasSelectionChanged(true);
  };

    const handleOk = () => {
    if (selectedRange !== 'custom') {
      const calculated = calculateRange(selectedRange, ianaTimezone, minDate, olderThan90DaysMode);

      onApply(selectedRange, calculated?.from, calculated?.to);
      return onClose();
    }

    if (range?.from && range?.to) {
      onApply('custom', range.from, range.to);
    } else {
      onApply(initialRange);
    }

    onClose();
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '100%', lg: '800px' },
        maxWidth: { xs: '100%', lg: '800px' },
        backgroundColor: colors.dateFilter.popoverBackground,
        borderRadius: '0px',
        boxShadow: colors.dateFilter.popoverShadow,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        border: `1px solid ${colors.dateFilter.popoverBorder}`,
        boxSizing: 'border-box',
      }}
    >
      {/* MAIN CONTENT */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        {/* LEFT — Fixed Time Range*/}
        <DateRangeSidebar
          selectedRange={selectedRange}
          handleFixedRange={(val: string) => { handleFixedRange(val); setHasSelectionChanged(val !== initialRange); }}
          handleCustomRange={() => { handleCustomRange(); setHasSelectionChanged(true); }}
          fixedOptions={fixedOptions}
          showCustomRange={showCustomRange}
        />

        {/* MIDDLE + RIGHT wrapper */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {/* MIDDLE — Absolute Time Range */}
          <AbsoluteInputs
            isCustom={isCustom}
            startDateInput={startDateInput}
            endDateInput={endDateInput}
            handleDateChange={wrappedHandleDateChange}
          />

          {/* Calendar */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: '12px 16px', md: '12px 10px', lg: '8px 16px' },
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: '100%', minWidth: 0 }}>
              {/* Nav Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  mt: { xs: 0, md: 1 },
                }}
              >
                <IconButton
                  onClick={() => changeMonth('prev')}
                  size="small"
                  disabled={!canGoPrev}
                  sx={{
                    border: canGoPrev ? `1px solid ${colors.dateFilter.triggerText}` : `1px solid ${colors.dateFilter.popoverBorder}`,
                    borderRadius: '4px',
                    p: '2px',
                    backgroundColor: canGoPrev ? colors.dateFilter.calendarBackground : colors.dateFilter.popoverBackground,
                    opacity: canGoPrev ? 1 : 0.35,
                    flexShrink: 0,
                    '&.Mui-disabled': { opacity: 0.35 },
                  }}
                >
                  <ChevronLeftIcon
                    sx={{
                      color: canGoPrev ? colors.dateFilter.triggerText : colors.dateFilter.weekDayText,
                      fontSize: '18px',
                    }}
                  />
                </IconButton>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '13px', md: '14px', lg: '15px' },
                    color: colors.dateFilter.dayText,
                    fontFamily: 'Montserrat',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mx: 1,
                  }}
                >
                  {moment.tz(month, ianaTimezone).format('MMM, YYYY')}
                </Typography>

                <IconButton
                  onClick={() => changeMonth('next')}
                  size="small"
                  disabled={!canGoNext}
                  sx={{
                    border: canGoNext ? `1px solid ${colors.dateFilter.triggerText}` : `1px solid ${colors.dateFilter.popoverBorder}`,
                    borderRadius: '4px',
                    p: '2px',
                    backgroundColor: canGoNext ? colors.dateFilter.calendarBackground : colors.dateFilter.popoverBackground,
                    opacity: canGoNext ? 1 : 0.35,
                    flexShrink: 0,
                    '&.Mui-disabled': { opacity: 0.35 },
                  }}
                >
                  <ChevronRightIcon
                    sx={{
                      color: canGoNext ? colors.dateFilter.triggerText : colors.dateFilter.weekDayText,
                      fontSize: '18px',
                    }}
                  />
                </IconButton>
              </Box>

              {/* DayPicker wrapper */}
              <Box
                sx={{
                  width: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  '& .rdp': { margin: 0, width: '100%' },
                  '& .rdp-months': { width: '100%' },
                  '& .rdp-month': { width: '100%' },
                  '& .rdp-table': {
                    width: '100%',
                    maxWidth: '100%',
                    tableLayout: 'fixed',
                  },
                  '& .rdp-head_cell': {
                    width: 'calc(100% / 7)',
                    textAlign: 'center',
                    fontSize: '11px',
                  },
                  '& .rdp-cell': {
                    width: 'calc(100% / 7)',
                    textAlign: 'center',
                    padding: '1px',
                  },
                  '& .rdp-day': {
                    margin: '0 auto',
                    width: '26px',
                    height: '26px',
                    fontSize: '11px',
                    cursor: isCustom ? 'pointer' : 'default',
                  },
                }}
              >
                <CalendarSection
                  isCustom={isCustom}
                  displayRange={displayRange}
                  setRange={(r: any) => { setRange(r); setHasSelectionChanged(true); }}
                  setSelectedRange={setSelectedRange}
                  setStartDateInput={setStartDateInput}
                  setEndDateInput={setEndDateInput}
                  month={month}
                  setMonth={setMonth}
                  ianaTimezone={ianaTimezone}
                  startDateFromBackend={startDateFromBackend}
                  availableDates={availableDates}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* BOTTOM BAR */ }
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 2 },
          backgroundColor: colors.dateFilter.presetPanelBackground,
          borderTop: `1px solid ${colors.dateFilter.popoverBorder}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '12px', md: '14px' },
            color: colors.dateFilter.dayText,
            fontFamily: 'Montserrat',
          }}
        >
          Timezone: <span style={{ fontWeight: 700 }}>{timezone}</span>
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: colors.dateFilter.dayText,
              fontSize: { xs: '13px', md: '15px' },
              fontWeight: 600,
              fontFamily: 'Montserrat',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={isOkDisabled ? undefined : handleOk}
            sx={{
              textTransform: 'none',
              color: isOkDisabled ? colors.dateFilter.weekDayText : colors.dateFilter.triggerText,
              fontSize: { xs: '13px', md: '15px' },
              fontWeight: 700,
              fontFamily: 'Montserrat',
              opacity: isOkDisabled ? 0.5 : 1,
              pointerEvents: isOkDisabled ? 'none' : 'auto',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            OK
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DateTimeRangePicker;
